/**
 * PÁGINA DE NOTICIAS
 * ================================
 * Listado de noticias con cards similares a la página de remates
 */

import React, { useState, useEffect } from 'react';
import { Newspaper, Calendar, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ModernHeader from '../components/modern/ModernHeader';
import ModernFooter from '../components/modern/ModernFooter';
import { api } from '../services/api';

// URL base para las imágenes de portada de noticias
const PORTADA_BASE_URL = 'https://plazarural.com.uy/uploads/noticias/';

const NoticiasPage = () => {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const [noticias, setNoticias] = useState([]);
  const [loading, setLoading] = useState(true);

  // Manejar scroll para header
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Cargar noticias desde la API
  useEffect(() => {
    const fetchNoticias = async () => {
      try {
        setLoading(true);
        const data = await api.getNews();
        console.log('[NoticiasPage] Noticias cargadas:', data);
        setNoticias(data);
      } catch (error) {
        console.error('[NoticiasPage] Error cargando noticias:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNoticias();
  }, []);

  // Formatear fecha
  const formatFecha = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('es-UY', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Navegar al detalle de la noticia por slug
  const handleVerNoticia = (slug) => {
    navigate(`/noticias/${slug}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-green-50">
      <ModernHeader scrollY={100} />

      <div className="pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4">

          {/* Header de la página */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2 flex items-center gap-3">
              <Newspaper className="w-10 h-10 text-green-600" />
              Noticias
            </h1>
            <p className="text-gray-600 text-lg">
              Mantente informado con las últimas novedades del sector
            </p>
          </div>

          {/* Loading state */}
          {loading && (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <Loader2 className="w-12 h-12 text-green-600 animate-spin mx-auto mb-4" />
                <p className="text-gray-600 font-medium">Cargando noticias...</p>
              </div>
            </div>
          )}

          {/* Grid de noticias */}
          {!loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {noticias.map(noticia => {
                const portadaUrl = noticia.image
                  ? `${PORTADA_BASE_URL}${noticia.image}`
                  : null;

                return (
                  <article
                    key={noticia.id}
                    className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-200 group cursor-pointer"
                    onClick={() => handleVerNoticia(noticia.slug)}
                  >
                    {/* Imagen de portada */}
                    <div className="relative h-56 bg-gradient-to-br from-green-100 to-green-200 overflow-hidden">
                      {portadaUrl ? (
                        <img
                          src={portadaUrl}
                          alt={noticia.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}

                      {/* Placeholder si no hay imagen */}
                      <div
                        className={`absolute inset-0 items-center justify-center ${portadaUrl ? 'hidden' : 'flex'}`}
                      >
                        <Newspaper className="w-20 h-20 text-green-300" />
                      </div>

                      {/* Badge de categoría */}
                      <div className="absolute top-4 left-4 bg-green-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                        {noticia.category}
                      </div>

                      {/* Overlay hover */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>

                    {/* Contenido */}
                    <div className="p-5">
                      {/* Fecha */}
                      <div className="flex items-center gap-2 text-gray-500 text-sm mb-3">
                        <Calendar className="w-4 h-4" />
                        <span>{formatFecha(noticia.date)}</span>
                      </div>

                      {/* Título */}
                      <h2 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-green-600 transition-colors">
                        {noticia.title}
                      </h2>

                      {/* Subtítulo */}
                      {noticia.subtitle && (
                        <p className="text-gray-600 text-sm line-clamp-2">
                          {noticia.subtitle}
                        </p>
                      )}

                      {/* Leer más */}
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <span className="text-green-600 font-semibold text-sm group-hover:underline">
                          Leer más →
                        </span>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          {/* Mensaje si no hay noticias */}
          {!loading && noticias.length === 0 && (
            <div className="text-center py-16">
              <Newspaper className="w-24 h-24 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-600 mb-2">
                No hay noticias disponibles
              </h3>
              <p className="text-gray-500">
                Vuelve pronto para ver las últimas novedades
              </p>
            </div>
          )}

        </div>
      </div>

      <ModernFooter />
    </div>
  );
};

export default NoticiasPage;